import { useEffect, useState } from "react";
import axios from "axios";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { AField, ATextArea, ImageUpload, SectionShell, SaveButton, Notice } from "./ui";

export const SettingsSection = () => {
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState({ error: "", success: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [logo, setLogo] = useState(null);
  const [logoMsg, setLogoMsg] = useState({ error: "", success: "" });
  const [savingLogo, setSavingLogo] = useState(false);
  const [inv, setInv] = useState(null);
  const [invMsg, setInvMsg] = useState({ error: "", success: "" });
  const [savingInv, setSavingInv] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then(({ data }) => setLogo(data.site?.logo_url || "")).catch(() => setLogo(""));
    axios
      .get(`${API}/admin/invoice-settings`, authHeaders())
      .then(({ data }) => setInv(data.invoice_settings))
      .catch(() => setInv({}));
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ error: "", success: "" });
    if (pw.new_password !== pw.confirm) {
      setPwMsg({ error: "New passwords do not match.", success: "" });
      return;
    }
    setSavingPw(true);
    try {
      await axios.put(
        `${API}/admin/auth/password`,
        { current_password: pw.current_password, new_password: pw.new_password },
        authHeaders()
      );
      setPwMsg({ error: "", success: "Password updated. Use it next time you sign in." });
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setPwMsg({ error: formatApiErrorDetail(err.response?.data?.detail), success: "" });
    } finally {
      setSavingPw(false);
    }
  };

  const saveLogo = async (e) => {
    e.preventDefault();
    setLogoMsg({ error: "", success: "" });
    setSavingLogo(true);
    try {
      await axios.put(`${API}/admin/content/site`, { value: { logo_url: logo } }, authHeaders());
      setLogoMsg({ error: "", success: "Logo updated — refresh the site to see it." });
    } catch (err) {
      setLogoMsg({ error: formatApiErrorDetail(err.response?.data?.detail), success: "" });
    } finally {
      setSavingLogo(false);
    }
  };

  const setI = (k) => (e) => {
    const v = typeof e === "object" && e?.target ? e.target.value : e;
    setInv((p) => ({ ...p, [k]: v }));
  };

  const saveInv = async (e) => {
    e.preventDefault();
    setInvMsg({ error: "", success: "" });
    setSavingInv(true);
    try {
      await axios.put(
        `${API}/admin/content/invoice_settings`,
        { value: inv },
        authHeaders()
      );
      setInvMsg({ error: "", success: "Invoice settings saved." });
    } catch (err) {
      setInvMsg({ error: formatApiErrorDetail(err.response?.data?.detail), success: "" });
    } finally {
      setSavingInv(false);
    }
  };

  if (logo === null || inv === null) return <p className="text-sm text-sage">Loading...</p>;

  return (
    <SectionShell title="Settings" subtitle="Admin password, site logo and invoice details." testid="admin-settings-section">
      <div className="space-y-10 max-w-5xl">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <form onSubmit={changePassword} className="bg-white border border-forest/10 p-6 sm:p-8 space-y-5">
            <h2 className="font-serif text-2xl font-medium text-forest">Change Password</h2>
            <Notice error={pwMsg.error} success={pwMsg.success} />
            <AField
              label="Current Password"
              testid="settings-current-password"
              type="password"
              required
              value={pw.current_password}
              onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
            />
            <AField
              label="New Password (min 8 characters)"
              testid="settings-new-password"
              type="password"
              required
              minLength={8}
              value={pw.new_password}
              onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
            />
            <AField
              label="Confirm New Password"
              testid="settings-confirm-password"
              type="password"
              required
              value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
            />
            <SaveButton saving={savingPw} testid="settings-password-save" label="Update Password" />
          </form>

          <form onSubmit={saveLogo} className="bg-white border border-forest/10 p-6 sm:p-8 space-y-5">
            <h2 className="font-serif text-2xl font-medium text-forest">Site Logo</h2>
            <Notice error={logoMsg.error} success={logoMsg.success} />
            <ImageUpload
              label="Logo Image"
              testid="settings-logo-upload"
              value={logo}
              onChange={setLogo}
              hint="PNG or SVG with a transparent background works best on the deep green header."
            />
            <SaveButton saving={savingLogo} testid="settings-logo-save" label="Update Logo" />
          </form>
        </div>

        <form
          onSubmit={saveInv}
          className="bg-white border border-forest/10 p-6 sm:p-8 space-y-6"
          data-testid="settings-invoice-form"
        >
          <div>
            <h2 className="font-serif text-2xl font-medium text-forest">Invoice Details</h2>
            <p className="text-[13px] text-sage mt-1">
              These appear on every invoice you issue. Leave the VAT number blank until you're VAT‑registered — invoices will be issued VAT‑free. Once set, you can turn VAT on or off per invoice.
            </p>
          </div>
          <Notice error={invMsg.error} success={invMsg.success} />

          <div>
            <h3 className="eyebrow text-gold-dark !mb-3">Company</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <AField
                label="Company Name"
                testid="inv-settings-company-name"
                value={inv.company_name || ""}
                onChange={setI("company_name")}
              />
              <AField
                label="VAT Number"
                testid="inv-settings-vat-number"
                value={inv.vat_number || ""}
                onChange={setI("vat_number")}
                placeholder="GB123456789"
                hint="Leave blank until you're VAT‑registered — invoices will skip VAT entirely."
              />
              <AField
                label="Business Email"
                testid="inv-settings-company-email"
                type="email"
                value={inv.company_email || ""}
                onChange={setI("company_email")}
              />
              <AField
                label="Business Phone"
                testid="inv-settings-company-phone"
                value={inv.company_phone || ""}
                onChange={setI("company_phone")}
              />
              <ATextArea
                label="Company Address"
                testid="inv-settings-company-address"
                rows={3}
                value={inv.company_address || ""}
                onChange={setI("company_address")}
              />
            </div>
          </div>

          <div>
            <h3 className="eyebrow text-gold-dark !mb-3">Bank Details (for payment on invoices)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <AField
                label="Bank Name"
                testid="inv-settings-bank-name"
                value={inv.bank_name || ""}
                onChange={setI("bank_name")}
              />
              <AField
                label="Account Name"
                testid="inv-settings-account-name"
                value={inv.account_name || ""}
                onChange={setI("account_name")}
              />
              <AField
                label="Sort Code"
                testid="inv-settings-sort-code"
                value={inv.sort_code || ""}
                onChange={setI("sort_code")}
                placeholder="12-34-56"
              />
              <AField
                label="Account Number"
                testid="inv-settings-account-number"
                value={inv.account_number || ""}
                onChange={setI("account_number")}
                placeholder="12345678"
              />
              <AField
                label="IBAN (optional)"
                testid="inv-settings-iban"
                value={inv.iban || ""}
                onChange={setI("iban")}
              />
            </div>
          </div>

          <div>
            <h3 className="eyebrow text-gold-dark !mb-3">Defaults for New Invoices</h3>
            <div className="space-y-5">
              <ATextArea
                label="Default Payment Terms"
                testid="inv-settings-default-terms"
                rows={2}
                value={inv.default_payment_terms || ""}
                onChange={setI("default_payment_terms")}
              />
              <ATextArea
                label="Default Notes"
                testid="inv-settings-default-notes"
                rows={2}
                value={inv.default_notes || ""}
                onChange={setI("default_notes")}
              />
            </div>
          </div>

          <SaveButton saving={savingInv} testid="inv-settings-save" label="Save Invoice Settings" />
        </form>
      </div>
    </SectionShell>
  );
};
